import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, User, Home, MapPin, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';

export interface AssignmentResponseMetadata {
  type: 'assignment_response';
  contactRequestId: string;
  action: 'accepted' | 'rejected';
  clientName: string;
  propertyTitle: string;
  propertyAddress: string;
  professionalMessage?: string;
}

interface AssignmentResponseCardProps {
  content: string;
  metadata: AssignmentResponseMetadata;
  timestamp: string;
  isOwn: boolean;
  senderName?: string;
}

export function AssignmentResponseCard({
  content,
  metadata,
  timestamp,
  isOwn,
  senderName,
}: AssignmentResponseCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isAccepted = metadata.action === 'accepted';
  const isProfessional = user?.role?.toLowerCase() === 'professional';

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleViewDetails = () => {
    if (isProfessional) {
      navigate(`/professional/leads/${metadata.contactRequestId}`);
    } else {
      navigate(`/admin/contact-requests/${metadata.contactRequestId}`);
    }
  };

  return (
    <div
      className={cn(
        'flex w-full mb-4',
        isOwn ? 'justify-end' : 'justify-start'
      )}
    >
      <div
        className={cn(
          'max-w-[85%] rounded-2xl shadow-sm overflow-hidden',
          isOwn
            ? 'bg-[#FF9F68] text-white rounded-br-md'
            : 'bg-white border border-gray-200 text-gray-900 rounded-bl-md'
        )}
      >
        {/* Header with sender name */}
        {!isOwn && senderName && (
          <div className="px-4 pt-3">
            <p className="text-xs font-medium text-[#FF9F68]">
              {senderName}
            </p>
          </div>
        )}

        {/* Status badge */}
        <div className="px-4 pt-3">
          <div className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium",
            isAccepted
              ? isOwn
                ? "bg-green-500/20 text-green-100"
                : "bg-green-100 text-green-700 border border-green-200"
              : isOwn
                ? "bg-red-500/20 text-red-100"
                : "bg-red-100 text-red-700 border border-red-200"
          )}>
            {isAccepted ? (
              <>
                <CheckCircle className="h-3.5 w-3.5" />
                Demande Acceptée
              </>
            ) : (
              <>
                <XCircle className="h-3.5 w-3.5" />
                Demande Refusée
              </>
            )}
          </div>
        </div>

        {/* Message content */}
        <div className="px-4 py-3">
          <p className={cn(
            "text-sm whitespace-pre-wrap leading-relaxed",
            isOwn ? "text-white" : "text-gray-700"
          )}>
            {content}
          </p>
        </div>

        {/* Preview Card */}
        <div
          className={cn(
            "mx-3 mb-3 p-4 rounded-xl cursor-pointer transition-all hover:shadow-md",
            isOwn
              ? "bg-white/10 hover:bg-white/20 border border-white/20"
              : "bg-gray-50 hover:bg-gray-100 border border-gray-200"
          )}
          onClick={handleViewDetails}
        >
          <div className="flex items-start gap-3">
            <div className={cn(
              "p-2 rounded-lg",
              isOwn ? "bg-white/20" : "bg-[#FF9F68]/10"
            )}>
              <User className={cn(
                "h-5 w-5",
                isOwn ? "text-white" : "text-[#FF9F68]"
              )} />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className={cn(
                "font-semibold text-sm truncate",
                isOwn ? "text-white" : "text-gray-900"
              )}>
                {metadata.clientName}
              </h4>
              <div className="flex items-center gap-1.5 mt-1">
                <Home className={cn(
                  "h-3.5 w-3.5 flex-shrink-0",
                  isOwn ? "text-blue-200" : "text-gray-500"
                )} />
                <span className={cn(
                  "text-xs truncate",
                  isOwn ? "text-blue-100" : "text-gray-600"
                )}>
                  {metadata.propertyTitle}
                </span>
              </div>
              {metadata.propertyAddress && (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <MapPin className={cn(
                    "h-3.5 w-3.5 flex-shrink-0",
                    isOwn ? "text-blue-200" : "text-gray-500"
                  )} />
                  <span className={cn(
                    "text-xs truncate",
                    isOwn ? "text-blue-100" : "text-gray-600"
                  )}>
                    {metadata.propertyAddress}
                  </span>
                </div>
              )}
            </div>
            <ExternalLink className={cn(
              "h-4 w-4 flex-shrink-0",
              isOwn ? "text-blue-200" : "text-gray-400"
            )} />
          </div>
        </div>

        {/* Timestamp */}
        <div className={cn(
          "px-4 pb-2 flex items-center",
          isOwn ? "justify-end" : "justify-start"
        )}>
          <span className={cn(
            "text-[10px]",
            isOwn ? "text-blue-100" : "text-gray-400"
          )}>
            {formatTime(timestamp)}
          </span>
        </div>
      </div>
    </div>
  );
}

export default AssignmentResponseCard;

